import { z } from 'zod';

/**
 * ============================================================================
 * GEO LOOKUP CONTRACT — GET /api/geo?q=<city>   (ADR-005 §5; rung-1 seam)
 * ============================================================================
 *
 * A minimal WORKER ROUTE for weather city search — the "type a city name, pick a
 * location" flow that lets the weather tile stop requiring raw lat/lon. It is
 * NOT a tile and NOT a new tile class: it is a small server-side helper endpoint,
 * a sibling of /api/tile, sharing the same guards and the same CloudFront-only
 * locking (both live under /api/*, already a LOCKED behavior in ADR-003 — the
 * Function URL is reachable only through CloudFront in the launch config).
 *
 * WHY SERVER-SIDE (not client-direct like rss.feed): geocoding uses OUR existing
 * OpenWeather key (the same key the weather adapter uses server-side, ADR-002 §5).
 * A key we pay for lives server-side and its results DEDUPE across users (every
 * "London" search hits one cache entry), so this is a shared-cache/proxy route, the
 * opposite of the user-source class. The client never sees the key or the OWM URL.
 *
 *   Endpoint:  GET /api/geo?q=<validated query>
 *   Response:  200 JSON { results: GeoResult[] }  (≤ GEO_MAX_RESULTS, possibly empty)
 *              400 JSON { error: string }          (q failed validation — never fetched)
 *
 * CACHING: results are cached via the SAME KvStore as /api/tile, keyed by the
 * NORMALIZED query (lowercased, collapsed whitespace — see geoCacheKey), TTL
 * GEO_TTL_SEC (24h; place names are effectively static). This keeps the OWM
 * geocoding call count bounded to distinct city strings, not users (D-007).
 *
 * SECURITY (same posture as /api/tile, ADR-002 §4/§7):
 *  - `q` is validated with `geoQuerySchema` BEFORE any upstream fetch (fail fast).
 *  - The pattern is a STRICT allowlist (letters/digits/space/comma/hyphen/period) —
 *    no URL/host/operator characters can reach the upstream request. This is the
 *    boundary guard; the OWM geocoding URL itself is built server-side only.
 *  - The response is Zod-validated (`geoResponseSchema`) before it is returned, so
 *    a malformed upstream payload degrades to an error, never a broken client.
 *  - Error messages never leak the key, the OWM URL, or upstream internals.
 *
 * These are TYPES + schemas + constants ONLY (the frozen contract). The worker
 * route implementation (parse → cache → OWM geocode → normalize) is the next
 * agent's; worker and app both import from here so they cannot drift.
 * ============================================================================
 */

/** Min/max length of the city query `q` (ADR-005 §5). Below MIN is not a real query. */
export const GEO_QUERY_MIN_CHARS = 2 as const;
export const GEO_QUERY_MAX_CHARS = 60 as const;

/**
 * STRICT allowlist pattern for the city query (ADR-005 §5): letters (incl. accented
 * ranges for international place names), digits, spaces, commas, hyphens and periods
 * ONLY — deliberately NO quotes/backslashes/operators/URL characters, so nothing
 * hostile can reach the upstream geocoding request. Empty is excluded by the length
 * floor. Examples that pass: "London", "São Paulo", "Washington, D.C.",
 * "Stratford-upon-Avon". Examples that fail: "a" (too short), 'x"y', "http://x".
 */
export const GEO_QUERY_PATTERN = /^[\p{L}\p{M}0-9 .,'\-]+$/u;

/** Max geocoding results returned (ADR-005 §5). Bounds the response + the picker UI. */
export const GEO_MAX_RESULTS = 5 as const;

/** Cache TTL for a geocoding result (seconds). 24h — place names are ~static (D-007). */
export const GEO_TTL_SEC = 24 * 60 * 60;

/** HTTP status codes this route emits (mirrors CACHE_HTTP; 200 envelope, 400 bad input). */
export const GEO_HTTP = {
  OK: 200,
  BAD_REQUEST: 400, // q failed validation — never fetched
} as const;

/**
 * The validated request query. `q` is the ONLY parameter. Validated server-side
 * with the strict pattern + length bounds BEFORE any upstream fetch (ADR-005 §5).
 */
export const geoQuerySchema = z
  .object({
    q: z
      .string()
      .min(GEO_QUERY_MIN_CHARS)
      .max(GEO_QUERY_MAX_CHARS)
      .regex(GEO_QUERY_PATTERN),
  })
  .strict();

export type GeoQuery = z.infer<typeof geoQuerySchema>;

/**
 * One geocoding hit — the shape the weather location picker consumes and the shape
 * a weather tile ultimately stores as `{lat, lon}` in its params. Deliberately
 * MINIMAL and PII-free:
 *   name    — human-readable place name ("London", "London, KY"). For display.
 *   lat/lon — coordinates (WGS-84). What the weather adapter actually needs. Range-
 *             bounded to match `weatherParamsSchema` (lat [-90,90], lon [-180,180]).
 *   country — ISO-3166 alpha-2 country code ("GB", "US"). Disambiguates same-named
 *             cities in the picker. 2 letters; not localized here.
 */
export const geoResultSchema = z
  .object({
    name: z.string().min(1),
    lat: z.number().min(-90).max(90),
    lon: z.number().min(-180).max(180),
    country: z.string().length(2),
  })
  .strict();

export type GeoResult = z.infer<typeof geoResultSchema>;

/**
 * The success response envelope. An array of ≤ GEO_MAX_RESULTS hits (possibly
 * EMPTY — an unknown city is a valid 200 with `results: []`, not an error). The
 * server validates the normalized OWM payload against this before returning it.
 */
export const geoResponseSchema = z
  .object({
    results: z.array(geoResultSchema).max(GEO_MAX_RESULTS),
  })
  .strict();

export type GeoResponse = z.infer<typeof geoResponseSchema>;

/** The 400 body when `q` fails validation. Never carries upstream internals. */
export interface GeoErrorResponse {
  readonly error: string;
}

/**
 * Normalize a raw city query into the CANONICAL cache-key string (ADR-005 §5):
 * trim, collapse internal whitespace to single spaces, and lowercase. So "  London  ",
 * "London", and "LONDON" all dedupe to one cached geocoding result (D-007). Pure &
 * total; assumes `q` already passed `geoQuerySchema` (callers validate first).
 */
export function normalizeGeoQuery(q: string): string {
  return q.trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * The KV cache key for a geocoding result. Versioned prefix (`geo:v1:`) so the
 * geo namespace can be invalidated independently of the tile cache, mirroring the
 * `v1|` prefix in deriveCacheKey. Built from the NORMALIZED query only.
 */
export function geoCacheKey(q: string): string {
  return `geo:v1:${normalizeGeoQuery(q)}`;
}
